import React from 'react';

const InfoIcon = ({ tooltip }) => {
  const [show, setShow] = React.useState(false);
  const timerRef = React.useRef(null);
  const handleEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 100);
  };
  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };
  return (
    <span
      className="info-icon-trigger"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      i
      {show && (
        <div className="info-icon-tooltip">
          {tooltip}
        </div>
      )}
    </span>
  );
};

const SectionBanner = ({ icon, title, color }) => (
  <div className="section-banner" style={{ '--banner-bg': color }}>
    <i className={icon}></i>
    <span className="banner-title">
      {title}
    </span>
  </div>
);

const INCOME_PREFERENCES = [
  { value: 'Dynamic', label: 'How much can I get?' },
  { value: '$', label: 'I want a specific dollar amount' },
  { value: '%', label: 'I want a percentage of my portfolio' },
];

const CURRENT_YEAR = new Date().getFullYear();

const StepRetirementGoals = ({ formData, updateFormData, onCalculate, prevStep, isCalculating, isLocked = false }) => {
  // Calculate older partner's age from birth years
  const birthYear1 = formData.birth_year_1 || CURRENT_YEAR - 67;
  const birthYear2 = formData.birth_year_2 || CURRENT_YEAR - 67;
  const age1Calc = CURRENT_YEAR - birthYear1;
  const age2Calc = CURRENT_YEAR - birthYear2;
  const isCouple = formData.couple === 'Yes';
  const age1 = isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;

  const retirementAge = formData.retirement_age || 0;
  const years = formData.years || 0;
  const endAge = retirementAge + years;
  const drawdownMethod = formData.drawdown_method || 'Dynamic';
  const lumpSumWithdrawals = formData.lump_sum_withdrawals || [];
  const stepDownWithdrawals = formData.step_down_withdrawals || [];

  const lumpSums = lumpSumWithdrawals.length > 0
    ? lumpSumWithdrawals
    : [{ amount: 0, age: '' }];

  const handleDrawdownMethodChange = (value) => {
    const updates = { drawdown_method: value };
    if (value !== '%') {
      updates.use_sis_minimum = false;
    }
    if (value !== '$') {
      updates.step_down_withdrawals = [];
    }
    if (value === '%') {
      updates.drawdown_input = 0.05;
    }
    if (value === '$') {
      updates.drawdown_input = 75000;
      updates.step_down_withdrawals = [{ amount: 75000, fromAge: retirementAge, toAge: endAge }];
    }
    updateFormData(updates);
  };

  const handleSisToggle = (checked) => {
    const updates = { use_sis_minimum: checked };
    if (checked) {
      updates.drawdown_input = 0;
    } else {
      updates.drawdown_input = 0.05;
    }
    updateFormData(updates);
  };

  const updateLumpSumRow = (index, field, value) => {
    const updated = lumpSums.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    updateFormData({ lump_sum_withdrawals: updated });
  };

  const addLumpSumRow = () => {
    updateFormData({ lump_sum_withdrawals: [...lumpSums, { amount: 0, age: '' }] });
  };

  const removeLumpSumRow = (index) => {
    if (index === 0) {
      const updated = [{ amount: 0, age: '' }, ...lumpSums.slice(1)];
      updateFormData({ lump_sum_withdrawals: updated });
    } else {
      updateFormData({ lump_sum_withdrawals: lumpSums.filter((_, i) => i !== index) });
    }
  };

  // Auto-sync step-down schedule when retirement age or years change
  React.useEffect(() => {
    if (drawdownMethod !== '$' || !stepDownWithdrawals.length) return;

    const currentFirstFrom = stepDownWithdrawals[0]?.fromAge;
    const currentLastTo = stepDownWithdrawals[stepDownWithdrawals.length - 1]?.toAge;

    // Only sync if values actually changed
    if (currentFirstFrom === retirementAge && currentLastTo === endAge) return;

    const updated = stepDownWithdrawals.map((step, i) => {
      if (i === 0) {
        return { ...step, fromAge: retirementAge };
      }
      return step;
    });
    // Ensure last step extends to endAge
    if (updated.length > 0) {
      updated[updated.length - 1] = { ...updated[updated.length - 1], toAge: endAge };
    }
    updateFormData({ step_down_withdrawals: updated });
  }, [retirementAge, endAge]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastLump = lumpSums[lumpSums.length - 1];
  const canAddLumpSum = lastLump && lastLump.amount !== 0 && lastLump.age !== '' && lastLump.age !== 0;

  const updateStepDown = (index, field, value) => {
    const updated = stepDownWithdrawals.map((step, i) => {
      if (i !== index) return step;
      const newStep = { ...step, [field]: value };
      return newStep;
    });

    if (field === 'toAge') {
      const toAge = parseInt(value);
      const trimmed = updated.slice(0, index + 1);
      if (toAge < endAge) {
        trimmed.push({ amount: 0, fromAge: toAge + 1, toAge: endAge });
      }
      updateFormData({ step_down_withdrawals: trimmed, drawdown_input: trimmed[0].amount });
    } else {
      updateFormData({ step_down_withdrawals: updated, drawdown_input: updated[0].amount });
    }
  };

  const resetStepDown = () => {
    updateFormData({
      step_down_withdrawals: [{ amount: 75000, fromAge: retirementAge, toAge: endAge }],
      drawdown_input: 75000,
    });
  };

  const buildToAgeOptions = (fromAge) => {
    const options = [];
    for (let a = fromAge; a <= endAge; a++) {
      options.push(a);
    }
    return options;
  };

  const validateLumpAge = (age) => {
    if (age === '' || age === 0) return null;
    if (age < age1) return `Age must be at least ${age1}`;
    if (age > endAge) return `Age must be at most ${endAge}`;
    return null;
  };

  return (
    <div className="retirement-form">
      {isLocked && (
        <div className="alert alert-warning mb-3 d-flex align-items-center">
          <i className="bi bi-lock-fill me-2"></i>
          <span>
            <strong>View Only:</strong> This scenario is locked. Click "Make Changes" above to edit.
          </span>
        </div>
      )}
      <fieldset disabled={isLocked} style={{ opacity: isLocked ? 0.7 : 1 }}>
      {/* Retirement Income Goals */}
      <SectionBanner
        icon="bi bi-bullseye"
        title="Retirement Income Goals"
        color="#072040"
      />

      {/* Income Preference */}
      <div className="row mb-3">
        <div className="col-md-5">
          <label htmlFor="income_preference" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Income Preference
            <InfoIcon tooltip="Choose how to structure retirement income withdrawals. 'How much can I get?' calculates the maximum sustainable withdrawal based on the portfolio. 'Fixed %' withdraws a constant percentage of the portfolio each year. 'Fixed $' withdraws a constant inflation-adjusted dollar amount annually." />
          </label>
          <select
            id="income_preference"
            className="form-select form-control"
            value={drawdownMethod}
            onChange={(e) => handleDrawdownMethodChange(e.target.value)}
          >
            {INCOME_PREFERENCES.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Bequest */}
        {drawdownMethod === 'Dynamic' && (
          <div className="col-md-5">
            <label htmlFor="desired_bequest" className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Desired Bequest ($)
              <InfoIcon tooltip="The real dollar value of the portfolio intended to be remaining at the end of the projection period. This amount will be preserved for inheritance or other purposes." />
            </label>
            <input
              type="number"
              className="form-control"
              id="desired_bequest"
              value={formData.desired_bequest || ''}
              placeholder="0"
              min="0"
              step="10000"
              onChange={(e) => updateFormData({ desired_bequest: parseFloat(e.target.value) || 0 })}
            />
          </div>
        )}

        {/* Dollar amount */}
        {drawdownMethod === '$' && (
          <div className="col-md-5">
            <label htmlFor="drawdown_input_dollar" className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Annual Withdrawal Amount ($)
              <InfoIcon tooltip="The annual dollar amount (in today's dollars) to withdraw from the portfolio each year during retirement. This amount is adjusted for inflation." />
            </label>
            <input
              type="number"
              className="form-control"
              id="drawdown_input_dollar"
              value={formData.drawdown_input || ''}
              placeholder="75000"
              min="0"
              step="1000"
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                const updatedSteps = (formData.step_down_withdrawals || []).map((step, i) =>
                  i === 0 ? { ...step, amount: val } : step
                );
                updateFormData({ drawdown_input: val, step_down_withdrawals: updatedSteps });
              }}
            />
          </div>
        )}

        {/* Withdrawal Rate % */}
        {drawdownMethod === '%' && (
          <div className="col-md-5">
            <label htmlFor="drawdown_input" className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Withdrawal Rate (%)
              <InfoIcon tooltip="The percentage of the portfolio to withdraw each year during retirement." />
            </label>
            <input
              type="number"
              className="form-control"
              id="drawdown_input"
              value={formData.use_sis_minimum ? '' : (formData.drawdown_input ? Math.round(formData.drawdown_input * 10000) / 100 : '')}
              placeholder={formData.use_sis_minimum ? 'SIS minimum' : '5.0'}
              min="0"
              max="100"
              step="0.5"
              disabled={formData.use_sis_minimum}
              onChange={(e) => updateFormData({ drawdown_input: (parseFloat(e.target.value) || 0) / 100 })}
            />
          </div>
        )}

      </div>

      {/* SIS Minimum checkbox */}
      {drawdownMethod === '%' && (
        <div className="mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="use_sis_minimum"
              checked={formData.use_sis_minimum || false}
              onChange={(e) => handleSisToggle(e.target.checked)}
            />
            <label className="form-check-label small fw-semibold" htmlFor="use_sis_minimum" style={{ color: '#072040' }}>
              Use SIS Minimum Drawdown Rates
              <InfoIcon tooltip="Use the Superannuation Industry Supervision (SIS) minimum drawdown rates based on age. These are the government-mandated minimum withdrawal percentages." />
            </label>
          </div>
        </div>
      )}

      {/* Step-Down Withdrawals */}
      {drawdownMethod === '$' && (
        <div className="mb-4">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            <i className="bi bi-staircase me-1"></i>
            Step-Down Withdrawal Schedule
            <InfoIcon tooltip="Define a schedule of changing withdrawal amounts over retirement. Each step specifies a dollar amount and the age range it applies to." />
          </label>

          {stepDownWithdrawals.map((step, index) => (
            <div key={index} className="row mb-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label small text-muted">
                  Amount ($)
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={step.amount || ''}
                  placeholder="0"
                  min="0"
                  step="1000"
                  onChange={(e) => updateStepDown(index, 'amount', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted">
                  From Age
                </label>
                <input
                  type="number"
                  className="form-control"
                  style={{ backgroundColor: '#f8f9fa' }}
                  value={step.fromAge || ''}
                  readOnly
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted">
                  To Age
                </label>
                <select
                  className="form-select form-control"
                  value={step.toAge || endAge}
                  onChange={(e) => updateStepDown(index, 'toAge', parseInt(e.target.value))}
                >
                  {buildToAgeOptions(step.fromAge || retirementAge).map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-1"
            onClick={resetStepDown}
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i>
            Reset Schedule
          </button>
        </div>
      )}

      {/* Lump Sum Transactions */}
      <div className="mb-3">
        <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
          <i className="bi bi-cash-coin me-1"></i>
          Lump Sum Transactions (Optional)
          <InfoIcon tooltip="Model one-off transactions before or during retirement. Enter withdrawals as positive numbers (e.g., buying a car or paying off debt) or contributions as negative numbers (e.g., inheritance or property sale proceeds). All amounts are in real dollars." />
        </label>
      </div>

      {lumpSums.map((row, index) => {
        const ageError = validateLumpAge(row.age);
        return (
          <div key={index} className="row mb-2 align-items-end">
            <div className="col-md-4">
              {index === 0 && (
                <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
                  Transaction Amount ($)
                  <InfoIcon tooltip="Enter withdrawal amount as positive number, or contribution as negative number. Can be used before or during retirement." />
                </label>
              )}
              <input
                type="number"
                className="form-control"
                value={row.amount || ''}
                placeholder="0"
                step="1000"
                onChange={(e) => updateLumpSumRow(index, 'amount', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="col-md-4">
              {index === 0 && (
                <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
                  Age at Transaction
                  <InfoIcon tooltip="Age when this transaction occurs. Can be any age from current age through retirement." />
                </label>
              )}
              <input
                type="number"
                className="form-control"
                style={ageError ? { borderColor: '#dc3545' } : undefined}
                value={row.age || ''}
                min={age1}
                max={endAge}
                onChange={(e) => updateLumpSumRow(index, 'age', parseInt(e.target.value) || 0)}
              />
              {ageError && (
                <div className="small text-danger mt-1">{ageError}</div>
              )}
            </div>
            <div className="col-md-2 d-flex align-items-end">
              {index === 0 ? (
                <button
                  type="button"
                  className="btn btn-sm btn-warning"
                  onClick={() => removeLumpSumRow(0)}
                  title="Reset row"
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeLumpSumRow(index)}
                  title="Remove row"
                >
                  <i className="bi bi-trash me-1"></i>Remove
                </button>
              )}
            </div>
          </div>
        );
      })}

      <p className="small text-muted mb-2" style={{ fontStyle: 'italic' }}>
        Positive = withdrawal, Negative = contribution
      </p>

      {canAddLumpSum && (
        <div className="mb-4">
          <button
            type="button"
            className="btn btn-sm btn-retirement-secondary"
            onClick={addLumpSumRow}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add Transaction
          </button>
        </div>
      )}

      {!canAddLumpSum && <div className="mb-4" />}

      {/* Navigation */}
      <div className="btn-group mt-4 pt-3 border-top">
        <button className="btn btn-secondary" onClick={prevStep}>
          Previous
        </button>
        <button
          className="btn text-white ms-2"
          style={{ backgroundColor: '#f0a500' }}
          onClick={onCalculate}
          disabled={isCalculating || isLocked}
        >
          {isCalculating ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Calculating...
            </>
          ) : (
            'Calculate'
          )}
        </button>
      </div>
      </fieldset>
    </div>
  );
};

export default StepRetirementGoals;
